/**
 * @file PriorityBadge.tsx
 * @description Slice 61 — tiny colored badge next to the status pill
 *   when a project's priority is "urgent" or "high". Quiet for
 *   "normal" and "low" (the default expectation) so the badge only
 *   appears when there's something to call out. Used in both the
 *   customer dashboard list and the admin all-projects list.
 * @module Components
 * @security Pure presentation; reads only from the already-trusted
 *   ProjectDto.
 */

import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProjectPriority } from '../../lib/projects-api';

interface PriorityBadgeProps {
  priority: ProjectPriority | null;
}

export function PriorityBadge({ priority }: PriorityBadgeProps): JSX.Element | null {
  const { t } = useTranslation();
  if (priority !== 'urgent' && priority !== 'high') return null;
  const cls = priority === 'urgent' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700';
  const dot = priority === 'urgent' ? 'bg-red-500' : 'bg-amber-500';
  return (
    <span className={`chip ${cls}`} title={t('project.priority_tooltip')}>
      <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {t(`project.priority.${priority}`)}
    </span>
  );
}
